import { Dialog, Button, InputField } from "@te-whatu-ora/anatomic";
import mediaQuery from "@utils/mediaQuery";
import { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import CheckBox from "./CheckBox";
import {
  FeedbackFormErrors,
  getValidationError,
  postFeedback,
} from "@utils/feedback";
import { OnboardingContext } from "@context/onboardingContext";

interface FormValue {
  email?: string;
  feedback: string;
  isMaoriPacific: boolean;
}
interface Props {
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  treatment: string;
  recommendationID: string;
  recommendationName: string;
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 530px;
  margin: 0 auto;
  padding: 16px;
  box-sizing: border-box;
  max-height: 85vh;
  overflow-y: auto;

  ${mediaQuery("tablet")`
    padding: 24px;
  `}
`;

const Title = styled.div`
  color: var(--text-primary);
  text-align: center;
  font-size: 20px;
  font-weight: var(--font-weight-bold);
  line-height: 1.2;
  max-width: 100%;
  margin-bottom: 16px;

  ${mediaQuery("tablet")`
    font-size: 30px;
    line-height: 36px;
    margin-bottom: 24px;
  `}
`;

const InputWrapper = styled.div`
  width: 100%;
  margin-bottom: 20px;

  ${mediaQuery("tablet")`
    margin-bottom: 40px;
  `}
`;

const Label = styled.label<{
  hasError?: boolean;
}>`
  display: flex;
  color: ${({ hasError }) => (!hasError ? "#000" : "#d02323")};
  text-align: center;
  font-family: Poppins;
  font-size: var(--font-size-xl);
  font-style: normal;
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-xl);
  margin-bottom: 8px;
`;

const CheckBoxWrapper = styled.div`
  align-self: flex-start;
  margin-bottom: 16px;
  width: 100%;

  ${mediaQuery("tablet")`
    margin-bottom: 24px;
  `}
`;

const PrivacyNotice = styled.div`
  width: 100%;
  text-align: center;
  font-size: 11px;
  line-height: 1.4;
  color: #666;
  margin-bottom: 16px;
  font-family: Poppins;

  ${mediaQuery("tablet")`
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 24px;
  `}
`;

const ErrorField = styled.div`
  color: #d02323;
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-xl);
`;

const GenericErrorMessage = styled.div`
  color: #d02323;
  font-size: var(--font-size-base);
  text-align: center;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #ffebee;
  border-radius: 4px;
  width: 100%;
`;

const FeedbackForm = ({
  visible,
  onVisibleChange,
  treatment,
  recommendationID,
  recommendationName,
}: Props) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const { userData } = useContext(OnboardingContext) || {};
  const [formValues, setFormValues] = useState<FormValue>({
    feedback: "",
    isMaoriPacific: false,
  });

  const [formErrors, setFormErrors] = useState<FeedbackFormErrors>({
    email: "",
    feedback: "",
  });

  const [genericErrorMessage, setGenericErrorMessage] = useState("");

  useEffect(() => {
    if (!visible && isFormSubmitted) {
      setIsFormSubmitted(false);
    }
  }, [visible, isFormSubmitted]);

  const resetFormValues = () => {
    setFormValues({
      email: "",
      feedback: "",
      isMaoriPacific: false,
    });
  };

  const sendFeedback = async () => {
    const { feedback, isMaoriPacific, email } = formValues;
    const { district, role, practiceArea, location } = userData || {};

    setGenericErrorMessage("");

    try {
      await postFeedback({
        email,
        district: district || "",
        role: role || "",
        practiceArea: practiceArea || "",
        location: location || "",
        recommendation: recommendationID, 
        recommendationName,
        feedback,
        isMaoriPacific,
      });
      setIsFormSubmitted(true);
      resetFormValues();
    } catch (error: any) {
      setIsFormSubmitted(false);
      if (error.response && error.response.data && error.response.data.message) {
        setGenericErrorMessage(error.response.data.message);
      } else if (error.message) {
        setGenericErrorMessage(`Failed to send feedback: ${error.message}`);
      } else {
        setGenericErrorMessage(
          "Failed to send feedback. Please try again later."
        );
      }
    }
  };

  const handleOnClose = async () => {
    if (isFormSubmitted) {
      onVisibleChange(false);
      setIsFormSubmitted(false);
    } else {
      const emailError = getValidationError(formValues, "email");
      const feedbackError = getValidationError(formValues, "feedback");
      const hasNoFormError = !emailError && !feedbackError;

      setFormErrors({
        email: emailError,
        feedback: feedbackError,
      });

      if (hasNoFormError) sendFeedback();
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    name: keyof FormValue
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: e.target.value,
    }));
  };

  return (
    <Dialog.Root
      open={visible}
      onOpenChange={(open: boolean) => {
        onVisibleChange(open);

        if (isFormSubmitted) {
          setIsFormSubmitted(false);
        }
      }}
    >
      <Dialog.Content style={{ maxHeight: '90vh', overflow: 'auto' }}>
        <ContentWrapper>
          <Title>
            {isFormSubmitted
              ? "Thanks for your feedback!"
              : `Share your feedback about ${treatment} guidelines`}
          </Title>
          {genericErrorMessage && (
            <GenericErrorMessage>{genericErrorMessage}</GenericErrorMessage>
          )}
          {!isFormSubmitted && (
            <>
              <InputWrapper>
                <Label hasError={Boolean(formErrors.email)}>
                  Email Address
                </Label>
                <InputField
                  type="email"
                  name="email"
                  onChange={(e) => handleChange(e, "email")}
                  error={Boolean(formErrors.email)}
                />
                {formErrors.email && (
                  <ErrorField>{formErrors.email}</ErrorField>
                )}
              </InputWrapper>
              <InputWrapper>
                <Label hasError={Boolean(formErrors.feedback)}>
                  Your feedback, particularly promoting equitable prescribing
                </Label>
                <InputField
                  multiline
                  type="text"
                  name="feedback"
                  onChange={(e) => handleChange(e, "feedback")}
                  error={Boolean(formErrors.feedback)}
                />
                {formErrors.feedback && (
                  <ErrorField>{formErrors.feedback}</ErrorField>
                )}
              </InputWrapper>
              <CheckBoxWrapper>
                <CheckBox
                  label={userData?.role !== "ACA Staff"
                    ? "I work for a Māori or Pacific healthcare provider."
                    : "No feedback - recommendation & flow are accurate"}
                  value="isMaoriPacific"
                  name="isMaoriPacific"
                  isChecked={formValues.isMaoriPacific}
                  onChange={() =>
                    setFormValues((prev) => ({
                      ...prev,
                      isMaoriPacific: !prev.isMaoriPacific,
                    }))
                  }
                />
                {userData?.role !== "ACA Staff" ? "This is to enable us to identify feedback from Māori and Pacific Providers" : ""}
              </CheckBoxWrapper>
              <PrivacyNotice>
                  Please do not include personally identifiable information (including patient details or identifiers) in the feedback box. 
                  Approved AI models will be used to collate feedback responses.
              </PrivacyNotice>
            </>
          )}

          <Button onClick={handleOnClose}>
            {isFormSubmitted ? "Back to guidelines" : "Send"}
          </Button>
        </ContentWrapper>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default FeedbackForm;